import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Cpu, MapPin, Calendar, Tag, AlertTriangle,
  ClipboardList, Activity, Clock, DollarSign,
} from 'lucide-react';
import { equipmentApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge, criticalityColor } from '@/components/ui/badge';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { AttachmentsPanel } from '@/components/AttachmentsPanel';
import { MeterPanel } from '@/components/MeterPanel';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMins(mins: number): string {
  if (mins === 0) return '0m';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function statusColor(s: string): 'green' | 'blue' | 'yellow' | 'red' | 'slate' {
  if (s === 'approved' || s === 'closed') return 'green';
  if (s === 'in_progress') return 'blue';
  if (s === 'awaiting_approval') return 'yellow';
  if (s === 'rejected') return 'red';
  return 'slate';
}


function KpiCard({
  label, value, sub, icon: Icon, color,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: typeof Cpu;
  color: 'indigo' | 'sky' | 'amber' | 'green';
}) {
  const chip = {
    indigo: 'bg-brand-700',
    sky:    'bg-sky-700',
    amber:  'bg-amber-700',
    green:  'bg-green-700',
  }[color];
  const border = {
    indigo: 'border-l-brand-600',
    sky:    'border-l-sky-600',
    amber:  'border-l-amber-600',
    green:  'border-l-green-600',
  }[color];
  return (
    <div className={`bg-white rounded-lg border border-canvas-line border-l-4 ${border} px-5 py-4 flex items-start gap-3`}>
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0 text-white ${chip}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] text-slate-600 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-semibold font-mono tracking-tight text-slate-900">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EquipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['equipment', 'detail', id],
    queryFn:  () => equipmentApi.detail(id!),
    enabled:  !!id,
  });

  if (isLoading) {
    return <p className="text-center text-slate-400 py-16">Loading…</p>;
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-slate-400">
        <AlertTriangle size={32} />
        <p>Equipment not found.</p>
        <Button variant="outline" onClick={() => navigate('/equipment')}>
          <ArrowLeft size={14} /> Back to Equipment
        </Button>
      </div>
    );
  }

  const { stats } = data;

  return (
    <div className="flex flex-col gap-5">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate('/equipment')}
          className="mt-1 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-slate-900">{data.name}</h1>
            <Badge color={criticalityColor(data.criticality)}>{data.criticality}</Badge>
          </div>
          <p className="text-sm text-slate-600 font-mono mt-0.5">{data.asset_tag}</p>
        </div>
      </div>

      {/* ── Info grid ──────────────────────────────────────────────────────── */}
      <Card>
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Cpu size={15} className="text-brand-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Type</p>
                <p className="font-medium text-slate-900 capitalize">{data.type}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={15} className="text-sky-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Location</p>
                <p className="font-medium text-slate-900">{data.location}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Tag size={15} className="text-violet-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Manufacturer / Model</p>
                <p className="font-medium text-slate-900">
                  {[data.manufacturer, data.model].filter(Boolean).join(' ') || '—'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar size={15} className="text-teal-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Install Date</p>
                <p className="font-medium text-slate-900">
                  {data.install_date ? new Date(data.install_date).toLocaleDateString() : '—'}
                </p>
              </div>
            </div>
            {data.asset_tag && (
              <div>
                <p className="text-xs text-slate-600 font-medium">Asset Tag</p>
                <p className="font-medium text-slate-900 font-mono text-xs">{data.asset_tag}</p>
              </div>
            )}
            {data.serial_number && (
              <div>
                <p className="text-xs text-slate-600 font-medium">Serial Number</p>
                <p className="font-medium text-slate-900 font-mono text-xs">{data.serial_number}</p>
              </div>
            )}
            {data.notes && (
              <div className="col-span-2 md:col-span-4">
                <p className="text-xs text-slate-600 font-medium mb-0.5">Notes</p>
                <p className="text-slate-700">{data.notes}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* ── KPI cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Work Orders" value={String(stats.total_work_orders)} icon={ClipboardList} color="indigo" />
        <KpiCard label="Open / Active" value={String(stats.open_work_orders)} icon={Activity} color="sky" />
        <KpiCard
          label="Total Downtime"
          value={fmtMins(stats.total_downtime_minutes)}
          icon={Clock}
          color="amber"
        />
        <KpiCard
          label="Total Repair Cost"
          value={stats.total_cost > 0 ? `$${stats.total_cost.toLocaleString()}` : '—'}
          icon={DollarSign}
          color="green"
        />
      </div>

      {/* ── Maintenance tasks + Recent WOs ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Maintenance tasks */}
        <Card>
          <CardHeader title={`Maintenance Tasks (${data.maintenance_tasks.length})`} />
          <CardBody className="p-0">
            {data.maintenance_tasks.length === 0 ? (
              <p className="px-5 py-6 text-center text-slate-400 text-sm">No tasks scheduled.</p>
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>Task</Th>
                    <Th>Type</Th>
                    <Th>Next Due</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {data.maintenance_tasks.map(t => (
                    <Tr key={t.id}>
                      <Td className="font-medium text-sm">{t.name}</Td>
                      <Td>
                        <Badge color={
                          t.task_type === 'corrective' ? 'red' :
                          t.task_type === 'preventive' ? 'blue' : 'purple'
                        }>
                          {t.task_type}
                        </Badge>
                      </Td>
                      <Td className="text-xs">
                        {t.next_due_at ? (
                          <span className={new Date(t.next_due_at) < new Date() ? 'text-red-600 font-medium' : ''}>
                            {fmtDate(t.next_due_at)}
                          </span>
                        ) : '—'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        {/* Recent work orders */}
        <Card>
          <CardHeader title={`Recent Work Orders (last ${data.recent_work_orders.length})`} />
          <CardBody className="p-0">
            {data.recent_work_orders.length === 0 ? (
              <p className="px-5 py-6 text-center text-slate-400 text-sm">No work orders yet.</p>
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>WO #</Th>
                    <Th>Title</Th>
                    <Th>Status</Th>
                    <Th>Downtime</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {data.recent_work_orders.map(wo => (
                    <Tr key={wo.id}>
                      <Td className="font-mono text-xs">{wo.wo_number}</Td>
                      <Td className="max-w-[10rem] truncate text-sm font-medium">{wo.title}</Td>
                      <Td>
                        <Badge color={statusColor(wo.status)}>
                          {wo.status.replace(/_/g, ' ')}
                        </Badge>
                      </Td>
                      <Td className={`text-sm ${wo.downtime_minutes ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                        {wo.downtime_minutes ? fmtMins(wo.downtime_minutes) : '—'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>

      {/* ── Meter Readings (Pro tier only) ─────────────────────────────────── */}
      <MeterPanel equipment={data} />

      {/* ── Attachments ────────────────────────────────────────────────────── */}
      <AttachmentsPanel entityType="equipment" entityId={id!} />

    </div>
  );
}
